import * as d3 from 'd3'
import * as _ from 'lodash'
import { Dim, Col } from './Dim'
import { SelectionState } from './Chart'
import { parseISO } from 'date-fns'
import { merge, capitalCase, toRecord } from '../common/Utils'

export interface Graph<N, L> {
  nodes: N
  links: L
}

export type Dic<T> = _.Dictionary<T>

export interface ChannelData {
  channelId: string
  title: string
  tags: string[]
  subCount: number
  channelVideoViews: number
  thumbnail: string
  lr: string
  publishedFrom: Date
  publishedTo: Date
  dailyViews: number
  relevantDailyViews: number
  relevance: number
  relevantImpressionsDaily: number
  relevantImpressionsDailyIn: number
  views: number
  community: string
  media: string,
  manoel: string,
  ain: string,
  sentiment: number,
  ngrams: string,
  svo: string,
  replied_to: string,
  size: number, 
  tweets: string,
  url1: string,
  url2: string,
  html1: string,
  html2: string
}

export class ChannelEx {
  static impressionAdvantagePercent = (c:ChannelData) => c.relevantImpressionsDailyIn / c.relevantImpressionsDaily - 1
}

export interface RecData {
  fromChannelId?: string
  toChannelId?: string
  fromTitle?: string,
  toTitle?: string,
  fromIdeology?: string
  toIdeology?: string
  fromLr?: string
  toLr?: string
  fromMedia?: string
  toMedia?: string
  fromManoel?: string
  toManoel?: string
  fromAin?: string
  toAin?: string
  relevantImpressionsDaily: number
  recommendsViewChannelPercent?: number
}

export type RecDir = 'from' | 'to'

export class RecEx {
  static recCol = (dir: RecDir, col: keyof ChannelData): keyof RecData =>
    `${dir}${_.upperFirst(col)}` as keyof RecData

  static channelCol = (col: keyof RecData|string): keyof ChannelData => {
    if (col.startsWith('from')) return _.lowerFirst(col.substr(4)) as keyof ChannelData
    if (col.startsWith('to')) return _.lowerFirst(col.substr(2)) as keyof ChannelData
    return null
  }
}

interface VIS_CHANNEL_REC {
  Weight: string,
  FROM_CHANNEL_ID: string,
  TO_CHANNEL_ID: string
}

export class YtModel {
  recs: Dim<RecData>
  recCats: Dim<RecData>
  channels: Dim<ChannelData>
  selectionState: SelectionState

  static version = 'v2.2';
  static async dataSet(path: string): Promise<YtModel> {
    const edges = d3.csv("https://gist.githubusercontent.com/CuriouzK0d3r/bd24ca60166fd3094f3bba0165603dd0/raw/b9c97331d1f362eb1ca336aca82c4a5f174686ac/edges.csv")
    const nodes = d3.json("https://gist.githubusercontent.com/CuriouzK0d3r/4ee19adbb3f74d18f32fb18b5870fff2/raw/73a2aed98b7296541072713051544323ffefef4c/nodes2.json")

    const channelsTask = d3.csv(path + 'vis_channel_stats.csv.gz')
    const recTask = d3.csv('' + 'https://gist.githubusercontent.com/CuriouzK0d3r/0e79accb5a8172a49de20353dbed7af9/raw/e45041f912ea3523121900241bdc95615f70396e/vis_channel_recs.csv')
    const recCatTask = d3.csv(path + 'vis_category_recs.csv.gz')
    let cnt = 0;
    const channels = (await nodes).map((c: any) => {
      console.log(c.size);
      
      return <ChannelData>{
        channelId: c.CHANNEL_ID,
        title: c.label,
        // tags: JSON.parse(c.TAGS),
        // subCount: c.SUBS,
        channelVideoViews: +c.size,
        replied_to: c.repliedto,
        tweets: c.tweet1 + '  |  ' + c.tweet2,
        url1: c.url1,
        url2: c.url2,
        html1: c.html1,
        html2: c.html2,
        // thumbnail: c.LOGO_URL,
        // lr: c.LR,
        // publishedFrom: parseISO(c.FROM_DATE),
        // publishedTo: parseISO(c.TO_DATE),
        // dailyViews: +c.DAILY_VIEWS,
        // relevantDailyViews: +c.RELEVANT_DAILY_VIEWS,
        // views: +c.CHANNEL_VIEWS,
        relevantImpressionsDaily: c.indegree/10,
        relevantImpressionsDailyIn: 1,
        sentiment: c.sentiment,
        ngrams: c.ngrams,
        svo: c.svo,
        community: c.community,
        size: parseInt(c.size),
        // media: c.MEDIA,
        // relevance: c.RELEVANCE,
        // manoel: c.MANOEL,
        // ain: c.AIN
      }
    })

    const channelDic = _(channels).keyBy(c => c.channelId).value()

    let recCol = (dir: RecDir, c: keyof ChannelData) => ({ recCol: RecEx.recCol(dir, c), channelCol: c, dir })

    const createRec = (r: VIS_CHANNEL_REC): RecData => {
      const recCols = _(YtModel.categoryCols.concat('channelId', 'title'))
        .flatMap(c => [recCol('from', c), recCol('to', c)]).value()
      let rec = merge<RecData>({
        recommendsViewChannelPercent: +parseFloat(r.Weight),
        relevantImpressionsDaily: +parseFloat(r.Weight)
      },
        toRecord(recCols,
          c => c.recCol,
          c => {
            const channel = channelDic[r[`${c.dir.toUpperCase()}_CHANNEL_ID` as keyof VIS_CHANNEL_REC]]
            return channel ? channel[c.channelCol] : null
          }))
      
      return rec
    }

    let recs = (await edges).map((r: any) => r as VIS_CHANNEL_REC).map(r => createRec(r))

    let recCats = (await recCatTask).map(r => {
      let recCols = _(YtModel.categoryCols)
        .flatMap(c => [RecEx.recCol('from', c), RecEx.recCol('to', c)]).value()

      let rec = merge<RecData>({
        relevantImpressionsDaily: +r.Weight
      }, toRecord(recCols, c => c, c => r[capitalCase(c)]?.toString()))
      return rec
    })

    return {
      channels: new Dim(this.channelDimStatic.meta, channels),
      recs: new Dim(this.recDimStatic.meta, recs),
      recCats: new Dim(this.recCatDimStatic.meta, recCats),
      selectionState: { selected: [], parameters: { record: { colorBy: 'community' } } },
    }
  }

  static categoryCols: (keyof ChannelData)[] = []//['ideology', 'lr', 'media', 'manoel', 'ain']

  static colors = [
   "#002366",
   "#800080",
   "#000080",
   "#97e2ff",
   "#7c4ea2",
   "#00b300",
   "#009a00",
   "#ffb733",
   "#f2d181",
   "#50cadc",
   "#aad6ff",
   "#50cadc",
   "#0241ae",
   "#024bc7",
   "#eeece8",
   "#e3e0d9",
   "#ea8d45",
   "#e7e5e2",
   "#dddad6",
   "#f0ede8",
   "#efece6",
   "#efece7",
   "#1a2a2c",
   "#194343",
   "#cc672a",
   "#ecece5",
   "#e4e4db",
   "#d2d2cf",
   "#eae9e3",
   "#e9e8e3",
   "#eae8e2",
   "#e01e5a",
   "#e61e50",
   "#e53974",
   "#e488a2",
   "#b351f7",
   "#811ec8",
   "#a50948",
   "#7e22df",
   "#8000ff",
   "#fa3b00",
   "#ce890a",
  ]

  static channelDimStatic = new Dim<ChannelData>({
    name: 'channel',
    cols: [
      {
        name: 'channelId',
        props: ['lr', 'community', 'media', 'manoel', 'ain'],
        labelCol: 'title'
      },
      {
        name: 'title',
        values: [
          { value: '', label: 'Non-Political' },
        ]
      },
      {
        name: 'lr',
        label: 'Left/Center/Right',
        values: [
          { value: 'L', label: 'Left', color: '#3887be' },
          { value: 'C', label: 'Center', color: '#8a8acb' },
          { value: 'R', label: 'Right', color: '#e0393e' },
          { value: '', label: 'Unclassified', color: '#555' }
        ]
      },
      {
        name: 'community',
        label: 'Ledwich & Zaitsev Group',
        pallet: ['#333'],
        values: [
          { value: '0', color: '#8a8acb' },
          { value: '1', color: '#e0393e' },
          { value: '2', color: '#e55e5e' },
          { value: '3', color: '#c68143' },
          { value: '4', color: '#ed6498' },
          { value: '5', color: '#56b881' },
          { value: '6', color: '#6ec9e0' },
          { value: '7', color: '#3887be' },
          { value: '8', color: '#96cbb3' },
          { value: '9', color: '#b7b7b7' },
          { value: '10', color: '#41afa5' },
          { value: '11', color: '#ffc168' },
          { value: '12', color: '#aa557f' },
          { value: '13', color: '#ac658f' },
          { value: '14', color: '#76abb3' },
          { value: '15', color: '#aaaa98' },
          { value: '16', color: YtModel.colors[0] },
          { value: '17', color: YtModel.colors[1] },
          { value: '18', color: YtModel.colors[2] },
          { value: '19', color: YtModel.colors[3] },
          { value: '20', color: YtModel.colors[4] },
          { value: '21', color: YtModel.colors[5] },
          { value: '22', color: YtModel.colors[6] },
          { value: '23', color: YtModel.colors[7] },
          { value: '24', color: YtModel.colors[8] },
          { value: '25', color: YtModel.colors[9] },
          { value: '26', color: YtModel.colors[10] },
          { value: '27', color: YtModel.colors[11] },
          { value: '28', color: YtModel.colors[12] },
          { value: '29', color: YtModel.colors[13] },
          { value: '30', color: YtModel.colors[14] },
          { value: '31', color: YtModel.colors[15] },
          { value: '32', color: YtModel.colors[16] },
          { value: '33', color: YtModel.colors[17] },
          { value: '34', color: YtModel.colors[18] },
          { value: '35', color: YtModel.colors[19] },
          { value: '36', color: YtModel.colors[20] },
          { value: '37', color: YtModel.colors[21] },
          { value: '38', color: YtModel.colors[22] }

        ]
      },
      {
        name: 'media',
        label: 'Media Type',
        values: [
          { value: '', label: 'Other', color: '#333' },
          { value: 'Mainstream Media', color: '#3887be' },
          { value: 'YouTube', label: 'YouTube Creator', color: '#e55e5e' },
          { value: 'Missing Link Media', color: '#41afa5' },
        ]
      },
      {
        name: 'manoel',
        label: 'Radicalization Pathways - Ribeiro et al.',
        values: [
          { value: 'Alt-light', color: '#e55e5e' },
          { value: 'IDW', color: '#8a8acb' },
          { value: 'Alt-right', color: '#c68143' },
          { value: 'Control', color: '#b7b7b7' },
          { value: '', label: 'Unclassified', color: '#333' },
        ]
      },
      {
        name: 'ain',
        label: 'Alternative Influence Network',
        values: [
          { value: '', label: 'Unclassified', color: '#333' },
          { value: 'AIN', color: '#41afa5' }
        ]
      }
    ]
  })

  private static recCol(dir: RecDir, name: keyof ChannelData) {
    const col = YtModel.channelDimStatic.col(name)
    return merge(col as any as Col<RecData>, {
      name: RecEx.recCol(dir, col.name),
      labelCol: col.labelCol ? RecEx.recCol(dir, col.labelCol) : null,
    })
  }

  static recDimStatic = new Dim<RecData>({
    name: 'Recommendations',
    cols: _(['channelId', 'title'] as (keyof ChannelData)[])
      .concat(YtModel.categoryCols)
      .flatMap(c => ([YtModel.recCol('from', c), YtModel.recCol('to', c)])).value()
  })

  static recCatDimStatic = new Dim<RecData>({
    name: 'Category Recommendations',
    cols: _(YtModel.categoryCols)
      .flatMap(c => ([YtModel.recCol('from', c), YtModel.recCol('to', c)])).value()
  })
}






